import {
    Box,
    Button,
    IconButton,
    TableCell,
    Tooltip,
    Typography,
    withStyles,
} from '@material-ui/core';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import CheckIcon from '@material-ui/icons/Check';
import React from 'react';
import { copyToClipboard } from '../../utils/clipboard';

export const RiderTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    body: {
        // 6px sides instead of 8px: with eight columns that reclaims ~32px of
        // dead gutter between columns without crowding the content.
        padding: '8px 6px',
        borderTop: '1px solid #EBEDF8',
        borderBottom: '1px solid #EBEDF8',
        overflow: 'hidden',
        '&:first-child': {
            paddingLeft: '12px',
            borderTopLeftRadius: '4px',
            borderBottomLeftRadius: '4px',
            borderLeft: '1px solid #EBEDF8',
        },
        '&:last-child': {
            paddingRight: '12px',
            borderTopRightRadius: '4px',
            borderBottomRightRadius: '4px',
            borderRight: '1px solid #EBEDF8',
        },
    },
}))(TableCell);

export const RiderTableHeadCell = withStyles((theme) => ({
    head: {
        backgroundColor: theme.palette.common.white,
        color: '#525050',
        fontWeight: 700,
        // Reduced from 16 so eight columns fit without wrapping the headers.
        fontSize: 14,
        letterSpacing: '0.144px',
        textTransform: 'capitalize',
        padding: '8px 6px',
        borderBottom: '1px solid #EBEDF8',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '&:first-child': { paddingLeft: '12px' },
        '&:last-child': { paddingRight: '12px' },
    },
    body: {
        fontSize: 14,
    },
}))(TableCell);

export const ActionIcon = withStyles(() => ({
    root: {
        border: '1px solid transparent',
        '&:hover': {
            backgroundColor: '#fff !important',
            border: '1px solid #c7c6e2 !important',
        },
    },
    rootDelete: {
        color: 'red',
        border: '1px solid transparent',
        '&:hover': {
            border: '1px solid #c7c6e2',
            backgroundColor: 'red',
            color: '#fff',
        },
    },
}))(
    // forwardRef so the buttons can be wrapped in a Tooltip, which needs to
    // attach a ref to its child to anchor the popper.
    React.forwardRef(
        (
            { classes, isDelete, useButton = false, ...props }: any,
            ref: React.Ref<any>,
        ) =>
            !!useButton ? (
                <Button
                    ref={ref}
                    classes={{
                        root: !!isDelete ? classes.rootDelete : classes.root,
                    }}
                    {...props}
                >
                    {props.children}
                </Button>
            ) : (
                <IconButton
                    ref={ref}
                    classes={{
                        root: !!isDelete ? classes.rootDelete : classes.root,
                    }}
                    {...props}
                >
                    {props?.children}
                </IconButton>
            ),
    ),
);

interface CellTextProps {
    /** Cell value — arrays are joined so multi-value columns stay single-line. */
    value?: any;
    /** Hard character cap (Zone/Hub run at 5). */
    maxChars?: number;
    /** Clip with CSS at the cell edge instead of counting characters. */
    ellipsis?: boolean;
    className?: string;
    /** Renders the value as a pill sized to its content. */
    chip?: boolean;
    /** Used for the placeholder so an empty cell isn't a coloured pill. */
    emptyClassName?: string;
    emptyText?: string;
    align?: string;
}

/**
 * Single-line table text that keeps the full value reachable through a tooltip
 * whenever the display value had to be shortened. With `chip`, the value is
 * rendered as a pill instead of plain text.
 */
export const CellText: React.FC<CellTextProps> = ({
    value,
    maxChars,
    ellipsis,
    className,
    chip = false,
    emptyClassName,
    emptyText = '-',
    align,
}) => {
    const fullText = Array.isArray(value)
        ? value
              .filter(
                  (item) => item !== null && item !== undefined && item !== '',
              )
              .join(', ')
        : value === null || value === undefined || value === ''
          ? ''
          : String(value);

    if (!fullText) {
        return (
            <Typography className={emptyClassName ?? className}>
                {emptyText}
            </Typography>
        );
    }

    const isCharTruncated = !!maxChars && fullText.length > maxChars;
    const displayText = isCharTruncated
        ? `${fullText.slice(0, maxChars)}…`
        : fullText;

    const text = (
        <Typography
            className={className}
            style={{
                // Under `table-layout: fixed` the cell already has a definite
                // width, so `100%` clips exactly at its edge — and because the
                // app is border-box, a chip's own padding is inside that cap.
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: align as any,
            }}
        >
            {displayText}
        </Typography>
    );

    // Only char-truncated cells are guaranteed to be clipped. For CSS-clipped
    // columns the tooltip is harmless and covers the overlap case the eye can't
    // predict from here.
    return isCharTruncated || ellipsis ? (
        <Tooltip title={fullText} arrow placement="top-start">
            {text}
        </Tooltip>
    ) : (
        text
    );
};

interface CopyableChipProps {
    /** Raw value shown in the pill and written to the clipboard. */
    value?: any;
    /** Pill styling (colour variant supplied by the page). */
    chipClassName?: string;
    /** Placeholder styling when there is no value. */
    emptyClassName?: string;
    emptyText?: string;
    /** Used in the tooltip, e.g. "phone number". */
    label?: string;
}

/** How long the button stays in its "copied" state before reverting. */
const COPIED_FEEDBACK_MS = 1400;

/**
 * Pill whose value can be copied either by double-clicking the pill itself or
 * through the adjacent copy button. Both paths swap the icon to a tick for a
 * moment so the copy is visibly confirmed without a toast on every click.
 */
export const CopyableChip: React.FC<CopyableChipProps> = ({
    value,
    chipClassName,
    emptyClassName,
    emptyText = '-',
    label = 'value',
}) => {
    const [copied, setCopied] = React.useState(false);
    const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // The row can unmount while the tick is still showing (paging, refetch),
    // so drop the pending timer rather than setting state on a dead component.
    React.useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        },
        [],
    );

    const text =
        value === null || value === undefined || value === ''
            ? ''
            : String(value);

    if (!text) {
        return <Typography className={emptyClassName}>{emptyText}</Typography>;
    }

    const handleCopy = async (event: React.SyntheticEvent) => {
        // Rows are clickable — never let a copy bubble into the row handler.
        event.stopPropagation();
        const ok = await copyToClipboard(text);
        if (!ok) return;
        setCopied(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(
            () => setCopied(false),
            COPIED_FEEDBACK_MS,
        );
    };

    return (
        <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            style={{ gap: 2 }}
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
        >
            <Tooltip
                title={copied ? 'Copied' : `Double-click to copy ${label}`}
                arrow
                placement="top-start"
            >
                <Typography
                    className={chipClassName}
                    onDoubleClick={handleCopy}
                    // minWidth:0 lets the pill shrink inside the flex row so it
                    // ellipsises rather than pushing the copy button out of view.
                    style={{ cursor: 'copy', minWidth: 0 }}
                >
                    {text}
                </Typography>
            </Tooltip>
            <Tooltip title={copied ? 'Copied' : `Copy ${label}`} arrow>
                <IconButton
                    size="small"
                    aria-label={`Copy ${label}`}
                    onClick={handleCopy}
                    style={{ padding: 4, flexShrink: 0 }}
                >
                    {copied ? (
                        <CheckIcon style={{ fontSize: 15, color: '#0f8a5f' }} />
                    ) : (
                        <FileCopyOutlinedIcon
                            style={{ fontSize: 14, color: '#8a92a6' }}
                        />
                    )}
                </IconButton>
            </Tooltip>
        </Box>
    );
};
